/** Shared types for the catalog. Mirrors Coworkcookbook/schemas/recipe.schema.json. */

export type PluginId =
  | "dynamics-365-erp"
  | "dynamics-365-cs"
  | "dynamics-365-sales"
  | "fabric-iq"
  | "monday-com"
  | "miro"
  | "prezi"
  | "none";

/** Human-readable plugin names. Recipe data stores slugs; the UI should never show them raw. */
export const PLUGIN_LABELS: Record<PluginId, string> = {
  "dynamics-365-erp": "Dynamics 365 ERP",
  "dynamics-365-cs": "Dynamics 365 Customer Service",
  "dynamics-365-sales": "Dynamics 365 Sales",
  "fabric-iq": "Fabric IQ",
  "monday-com": "monday.com",
  miro: "Miro",
  prezi: "Prezi",
  // "none" means no plugin is required to run the recipe. The recipe may still ship an
  // installable custom skill of its own - see recipe_type and uses_skills.custom.
  none: "No plugin required",
};

/** Falls back to the raw slug so an unmapped plugin degrades to something readable. */
export function pluginLabel(id: string): string {
  return PLUGIN_LABELS[id as PluginId] ?? id;
}
export type Difficulty = "beginner" | "intermediate" | "advanced";
export type RecipeType = "prompt" | "prompt+skill" | "prompt+blueprint";
export type GeneratedBy = "copilot" | "human";
export type RecipeStatus = "draft" | "verified";

/** A variable the blueprint diagram references and prompt.md must bind. */
export interface BlueprintInput {
  name: string;
  value: string;
  description?: string;
}

export interface Blueprint {
  /** Mermaid source filename in the recipe folder. */
  mermaid: string;
  /** Rendered diagram filename — the file the user pastes into Cowork. */
  image: string;
  source_node?: string;
  inputs: BlueprintInput[];
  clarifying_questions_asked?: number;
}

export interface PluginSkillRef {
  plugin: string;
  action: string;
}

export interface UsesSkills {
  ootb: string[];
  plugin: PluginSkillRef[];
  custom: string[];
}

export interface Author {
  github: string;
  name?: string;
}

export interface Recipe {
  id: string;
  title: string;
  summary: string;
  business_value?: string;
  plugin: PluginId;
  process_tags: string[];
  recipe_type: RecipeType;
  difficulty: Difficulty;
  mutates_data: boolean;
  min_plugin_version?: string;
  generated_by: GeneratedBy;
  reviewed_by: string;
  status: RecipeStatus;
  last_verified_on?: string;
  verified_against_cowork_build?: string;
  deprecated?: boolean;
  deprecated_reason?: string;
  license: "CC-BY-4.0" | "CC0-1.0" | "MIT";
  uses_skills: UsesSkills;
  blueprint?: Blueprint;
  prerequisites?: string[];
  youtube?: string[];
  authors: Author[];
  created: string;
  version: string;
}

export interface RecipeAsset {
  /** Path relative to /assets/<id>/ as served by SWA. */
  src: string;
  /** Required alt text for screenshots. */
  alt: string;
}

export interface SkillCompanion {
  /** Relative path under skill/, e.g. "references/implementation-register.md". */
  path: string;
  /** Anchor ID used for in-page navigation when rendered. */
  anchor_id: string;
  /** Public asset URL, e.g. "/assets/<id>/skill/references/implementation-register.md". */
  asset_url: string;
  /** Sanitized HTML rendered from markdown companion files (null for non-markdown). */
  html: string | null;
}

export interface RecipeDocument extends Recipe {
  /** Sanitized HTML rendered from README.md at build time. */
  description_html: string;
  /** The copyable prompt text (raw). */
  prompt_text: string;
  /** Sanitized HTML rendered from SKILL.md body (if a skill is present). */
  skill_html: string | null;
  /** SKILL.md YAML frontmatter, parsed (if present). */
  skill_meta: { name?: string; description?: string } | null;
  /** Companion files in the skill folder (relative paths under skill/). */
  skill_companion_files: string[];
  /** Rendered metadata and HTML for companion files in the skill folder. */
  skill_companions?: SkillCompanion[];
  /** Skill zip availability + SHA-256 (filled if a packaged skill exists). */
  skill_zip: { available: boolean; sha256?: string } | null;
  /** Raw mermaid source, inlined so the page can offer copy-to-clipboard. */
  blueprint_mermaid: string | null;
  /** Screenshots (built from screenshots/* and the markdown image refs). */
  screenshots: RecipeAsset[];
  /** Recipe folder slug (e.g., source-to-pay/vendor-invoice-three-way-match-status). */
  slug: string;
}

export type RecipeCategory =
  | "audit"
  | "report"
  | "dashboard"
  | "scheduled-brief"
  | "demo-data"
  | "configure"
  | "ppt-exec"
  | "teams-update"
  | "adaptive-card"
  | "bulk-update"
  | "blueprint"
  | "other";

export interface RecipeSummary {
  id: string;
  title: string;
  summary: string;
  plugin: PluginId;
  process_tags: string[];
  recipe_type: RecipeType;
  difficulty: Difficulty;
  mutates_data: boolean;
  deprecated: boolean;
  status: RecipeStatus;
  last_verified_on?: string;
  uses_skills: UsesSkills;
  category?: RecipeCategory;
  slug: string;
  version: string;
}

export interface Catalog {
  generated_at: string;
  recipes_sha?: string;
  recipes: RecipeSummary[];
}

export interface TaxonomyNode {
  id: string;
  title: string;
  description?: string;
  level: "end-to-end" | "area" | "process" | "activity";
  parent?: string;
  bpmn?: string;
  ms_bpc_id?: string;
  sequence_id?: string;
  learn_url?: string;
}

export interface TaxonomyDoc {
  version: string;
  source?: string;
  nodes: TaxonomyNode[];
  /** bpmn file references (just relative paths, content served from /data/bpmn/). */
  bpmn_files: string[];
}

export interface CoworkSkillsDoc {
  version: string;
  source?: string;
  ootb: { name: string; description: string }[];
  plugins: {
    id: string;
    name: string;
    description: string;
    docs?: string;
    actions: { id: string; name: string; description?: string }[];
  }[];
}
